import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {
  isPushSupported,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from '../services/pushNotifications';

const PushNotificationPrompt: React.FC = () => {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      if (!isPushSupported()) return;
      setSupported(true);
      const sub = await getExistingSubscription();
      setSubscribed(!!sub);
    };
    check();
  }, []);

  if (!supported) return null;

  const handleToggle = async () => {
    setLoading(true);
    setError(null);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
      } else {
        const success = await subscribeToPush();
        if (success) {
          setSubscribed(true);
        } else {
          setError('Notification permission was denied. Please enable notifications in your browser settings.');
        }
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {subscribed ? (
              <NotificationsActiveIcon color="success" />
            ) : (
              <NotificationsOffIcon color="action" />
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Push Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subscribed
                  ? 'You will receive event reminders on this device'
                  : 'Get notified about upcoming events on this device'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant={subscribed ? 'outlined' : 'contained'}
            onClick={handleToggle}
            disabled={loading}
            size="small"
          >
            {loading ? 'Working...' : subscribed ? 'Disable' : 'Enable'}
          </Button>
        </Box>
        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationPrompt;
